import { useEffect } from "react";
import { CalendarOutlined, MailOutlined, PhoneOutlined, EnvironmentOutlined } from "@ant-design/icons";
import { globalAppInfo } from "../../config/globalAppInfo";
import "./PrivacyPolicy.css";

const PrivacyPolicy = () => {
  const lastUpdated = "February 18, 2026";

  useEffect(() => {
    // Scroll to top immediately on component mount
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  return (
    <div className="legal-page">
      {/* Header */}
      <div className="legal-header">
        <div className="container">
          
          <div className="legal-header-icon">🔒</div>
          <h1>Privacy Policy</h1>
          <p className="legal-subtitle">
            We are committed to protecting your personal information and your right to privacy.
          </p>
          <p className="legal-last-updated">
            <CalendarOutlined /> Last Updated: {lastUpdated}
          </p>
        </div>
      </div>

      {/* Single Document Container */}
      <div className="legal-content">
        <div className="container">
          <div className="legal-document">

            <p className="legal-intro">
              Welcome to <strong>{globalAppInfo.name}</strong>. This Privacy Policy explains how we collect,
              use, disclose, and safeguard your information when you use our WhatsApp Business API messaging
              platform and related services. By accessing or using our services, you agree to this Privacy
              Policy. If you do not agree, please do not access the platform.
            </p>

            <div className="legal-toc">
              <h4>Table of Contents</h4>
              <ol>
                <li><a href="#pp-1">Information We Collect</a></li>
                <li><a href="#pp-2">How We Use Your Information</a></li>
                <li><a href="#pp-3">Information Sharing and Disclosure</a></li>
                <li><a href="#pp-4">Data Security</a></li>
                <li><a href="#pp-5">Data Retention</a></li>
                <li><a href="#pp-6">Your Rights and Choices</a></li>
                <li><a href="#pp-7">Cookies and Tracking Technologies</a></li>
                <li><a href="#pp-8">Third-Party Links</a></li>
                <li><a href="#pp-9">Children's Privacy</a></li>
                <li><a href="#pp-10">International Data Transfers</a></li>
                <li><a href="#pp-11">Changes to This Policy</a></li>
                <li><a href="#pp-12">Contact Us</a></li>
              </ol>
            </div>

            <h2 id="pp-1">1. Information We Collect</h2>

            <h3>1.1 Personal Information</h3>
            <p>We may collect personal information that you voluntarily provide to us when you register for an account, subscribe to our services, contact us for support, or participate in surveys or promotions. This information may include:</p>
            <ul>
              <li>Name and contact information (email address, phone number)</li>
              <li>Business name and details</li>
              <li>Billing and payment information</li>
              <li>WhatsApp Business account details</li>
              <li>Login credentials</li>
            </ul>

            <h3>1.2 Message Data</h3>
            <p>When you use our platform to send WhatsApp messages, we process and store:</p>
            <ul>
              <li>Message templates and content</li>
              <li>Contact lists and recipient information</li>
              <li>Message delivery status and analytics</li>
              <li>Campaign performance data</li>
            </ul>

            <h3>1.3 Automatically Collected Information</h3>
            <p>We automatically collect certain information when you access our platform, including device information (browser type, operating system), IP address and location data, usage patterns and preferences, and cookies and similar tracking technologies.</p>

            <h2 id="pp-2">2. How We Use Your Information</h2>
            <p>We use the collected information for the following purposes:</p>
            <ul>
              <li><strong>Service Delivery:</strong> To provide, maintain, and improve our WhatsApp messaging platform.</li>
              <li><strong>Account Management:</strong> To create and manage your user account.</li>
              <li><strong>Communication:</strong> To send you service-related notifications, updates, and promotional materials.</li>
              <li><strong>Analytics:</strong> To analyze usage patterns and improve our services.</li>
              <li><strong>Billing:</strong> To process payments and manage subscriptions.</li>
              <li><strong>Compliance:</strong> To comply with legal obligations and enforce our terms.</li>
              <li><strong>Security:</strong> To detect, prevent, and address technical issues and security threats.</li>
            </ul>

            <h2 id="pp-3">3. Information Sharing and Disclosure</h2>
            <p>We may share your information in the following circumstances:</p>

            <h3>3.1 Service Providers</h3>
            <p>We may share your information with third-party service providers who perform services on our behalf, including payment processing, data analysis, email delivery, and customer support.</p>

            <h3>3.2 WhatsApp / Meta</h3>
            <p>As a WhatsApp Business API provider, certain information is shared with Meta/WhatsApp as required to facilitate message delivery and comply with their policies.</p>

            <h3>3.3 Legal Requirements</h3>
            <p>We may disclose your information if required by law, legal process, or government request, or to protect our rights, privacy, safety, or property.</p>

            <h3>3.4 Business Transfers</h3>
            <p>In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of the transaction. We will notify you before your information becomes subject to a different privacy policy.</p>

            <h2 id="pp-4">4. Data Security</h2>
            <p>We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:</p>
            <ul>
              <li>End-to-end encryption for message transmission</li>
              <li>Secure data storage with encryption at rest</li>
              <li>Regular security audits and vulnerability assessments</li>
              <li>Access controls and authentication mechanisms</li>
              <li>Employee training on data protection practices</li>
            </ul>
            <p>However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.</p>

            <h2 id="pp-5">5. Data Retention</h2>
            <p>We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. Message data and campaign analytics are typically retained for a period of 12 months, after which they may be anonymized or deleted.</p>

            <h2 id="pp-6">6. Your Rights and Choices</h2>
            <p>Depending on your location, you may have the following rights:</p>
            <ul>
              <li><strong>Access:</strong> Request access to your personal information.</li>
              <li><strong>Correction:</strong> Request correction of inaccurate data.</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information.</li>
              <li><strong>Portability:</strong> Request a copy of your data in a portable format.</li>
              <li><strong>Opt-out:</strong> Opt out of marketing communications.</li>
              <li><strong>Restriction:</strong> Request restriction of processing.</li>
            </ul>
            <p>To exercise these rights, please contact us at <a href={`mailto:${globalAppInfo.email1}`}>{globalAppInfo.email1}</a>.</p>

            <h2 id="pp-7">7. Cookies and Tracking Technologies</h2>
            <p>We use cookies and similar tracking technologies to enhance your experience on our platform. These help us remember your preferences and settings, analyze traffic and usage patterns, provide personalized content and features, and ensure security and prevent fraud. You can manage your cookie preferences through your browser settings; however, disabling certain cookies may affect the functionality of our platform.</p>

            <h2 id="pp-8">8. Third-Party Links</h2>
            <p>Our platform may contain links to third-party websites or services. We are not responsible for the privacy practices of these third parties. We encourage you to review the privacy policies of any third-party sites you visit.</p>

            <h2 id="pp-9">9. Children's Privacy</h2>
            <p>Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected information from a child without parental consent, we will take steps to delete that information promptly.</p>

            <h2 id="pp-10">10. International Data Transfers</h2>
            <p>Your information may be transferred to and processed in countries other than your country of residence. These countries may have different data protection laws. When we transfer your information, we ensure appropriate safeguards are in place to protect your data in accordance with this Privacy Policy.</p>

            <h2 id="pp-11">11. Changes to This Privacy Policy</h2>
            <p>We may update this Privacy Policy from time to time. We will notify you of any significant changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. We encourage you to review this Privacy Policy periodically to stay informed about how we protect your information.</p>

            <h2 id="pp-12">12. Contact Us</h2>
            <p>If you have any questions or concerns about this Privacy Policy or our data practices, please contact us:</p>

            <div className="legal-contact-box">
              <p className="legal-contact-name">{globalAppInfo.name}</p>
              <p><EnvironmentOutlined className="legal-contact-icon" />{globalAppInfo.addressline1}</p>
              <p><MailOutlined className="legal-contact-icon" />Email: <a href={`mailto:${globalAppInfo.email1}`}>{globalAppInfo.email1}</a></p>
              <p><PhoneOutlined className="legal-contact-icon" />Phone: {globalAppInfo.mobile1}</p>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
